import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  acknowledgeNutritionHydrationAction, executeReviewedNutritionHydrationAction, readPendingNutritionHydrationAction,
  restoreNutritionHydrationActionReceipt, nutritionHydrationAuthSessionId, nutritionHydrationOwnerSession,
  releaseFailedNutritionHydrationAction,
  recoverPendingNutritionHydrationAction,
  type NutritionHydrationActionRequest, type NutritionHydrationResponse, type NutritionHydrationOwnerGuard,
} from '@/lib/nutritionHydrationExecutor';
import type { PendingConnectorWrite } from '@/lib/connectorPendingWrite';

/** One original review survives an unknown outcome; recovery never dispatches a new write. */
export function useNutritionHydrationActionConfirm(ownerId: string, isOriginCurrent: () => boolean, refreshToken = 0) {
  const [pending, setPending] = useState<PendingConnectorWrite | null>(null);
  const [ready, setReady] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState<string | null>(null);
  const epoch = useRef(0), mounted = useRef(false), lock = useRef(false), readyRef = useRef(false);
  const authSession = useRef<string | null>(null), origin = useRef(isOriginCurrent); origin.current = isOriginCurrent;
  const pendingRef = useRef<PendingConnectorWrite | null>(null);
  const replace = useCallback((entry: PendingConnectorWrite | null) => { pendingRef.current = entry; setPending(entry); }, []);
  useEffect(() => {
    mounted.current = true; const run = ++epoch.current;
    readyRef.current = false; authSession.current = null; lock.current = false; setReady(false); setBusy(false); replace(null); setError(null);
    const current = () => mounted.current && run === epoch.current && origin.current();
    const assert: NutritionHydrationOwnerGuard = () => { if (!current()) throw new Error('invalid_session'); };
    const retire = () => { epoch.current++; readyRef.current = false; lock.current = true; authSession.current = null; setReady(false); setBusy(false); replace(null); setError('invalid_session'); };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const id = nutritionHydrationAuthSessionId(session?.access_token);
      if (session?.user.id !== ownerId || !id || authSession.current && authSession.current !== id) retire();
      else authSession.current = id;
    });
    if (ownerId) void (async () => {
      try {
        const session = await nutritionHydrationOwnerSession(ownerId, assert); assert();
        const id = nutritionHydrationAuthSessionId(session.access_token); if (!id || authSession.current && authSession.current !== id) throw new Error('invalid_session');
        authSession.current = id; assert.authSessionId = id;
        const saved = await readPendingNutritionHydrationAction(ownerId, assert); assert();
        replace(saved); readyRef.current = true; setReady(true);
      } catch (cause) { if (current()) setError(cause instanceof Error ? cause.message : 'pending_storage_unavailable'); }
    })();
    const lifecycle = epoch;
    return () => { mounted.current = false; lifecycle.current++; readyRef.current = false; subscription.unsubscribe(); };
  }, [ownerId, replace, refreshToken]);
  const captureCurrent = (): NutritionHydrationOwnerGuard => {
    const run = epoch.current, authId = authSession.current;
    const assert: NutritionHydrationOwnerGuard = () => {
      if (!mounted.current || !readyRef.current || run !== epoch.current || !authId || authSession.current !== authId || !origin.current()) throw new Error('invalid_session');
    };
    if (authId) assert.authSessionId = authId;
    return assert;
  };
  const execute = async (input?: NutritionHydrationActionRequest) => {
    if (!readyRef.current || lock.current) return null;
    // Capture and clone the original before auth/journal awaits; never reread mutable input later.
    const original = input ?? pendingRef.current?.body; if (!original) { setError('review_required'); return null; }
    let request: NutritionHydrationActionRequest;
    try { request = structuredClone(original) as NutritionHydrationActionRequest; } catch { setError('invalid_request'); return null; }
    const assert = captureCurrent(); lock.current = true; setBusy(true); setError(null);
    try { assert(); const done = await executeReviewedNutritionHydrationAction(ownerId, request, assert); assert(); replace(done.entry); return done; }
    catch (cause) {
      try {
        assert(); setError(cause instanceof Error ? cause.message : 'outcome_unknown');
        const saved = await readPendingNutritionHydrationAction(ownerId, assert); assert(); replace(saved);
      } catch { try { assert(); setError('pending_storage_unavailable'); } catch { /* retired actor */ } }
      return null;
    } finally { try { assert(); lock.current = false; setBusy(false); } catch { /* retired actor */ } }
  };
  const restoreReceipt = async (input: NutritionHydrationResponse) => {
    if (!readyRef.current || lock.current) throw new Error('invalid_session');
    const original = structuredClone(input), assert = captureCurrent();
    lock.current = true; setBusy(true);
    try { const entry = await restoreNutritionHydrationActionReceipt(ownerId, original, assert); assert(); replace(entry); return entry; }
    finally { try { assert(); lock.current = false; setBusy(false); } catch { /* retired actor */ } }
  };
  const recover = async () => {
    const entry = pendingRef.current;
    if (!entry || !readyRef.current || lock.current) return null;
    const original = structuredClone(entry), assert = captureCurrent(); lock.current = true; setBusy(true); setError(null);
    try { const done = await recoverPendingNutritionHydrationAction(original, assert); assert(); replace(done.entry); return done; }
    catch (cause) { try { assert(); setError(cause instanceof Error ? cause.message : 'outcome_unknown'); } catch { /* retired actor */ } return null; }
    finally { try { assert(); lock.current = false; setBusy(false); } catch { /* retired actor */ } }
  };
  const acknowledge = async () => {
    const entry = pendingRef.current;
    if (!entry || entry.status !== 'succeeded' || !readyRef.current || lock.current) return false;
    const original = structuredClone(entry), assert = captureCurrent(); lock.current = true; setBusy(true); setError(null);
    try { await acknowledgeNutritionHydrationAction(original, assert); assert(); replace(null); return true; }
    catch (cause) { try { assert(); setError(cause instanceof Error ? cause.message : 'outcome_unknown'); } catch { /* retired */ } return false; }
    finally { try { assert(); lock.current = false; setBusy(false); } catch { /* retired */ } }
  };
  const releaseFailure = async () => {
    const entry = pendingRef.current;
    if (!entry || entry.status !== 'pending' || !readyRef.current || lock.current) return false;
    const original = structuredClone(entry), assert = captureCurrent(); lock.current = true; setBusy(true);
    try { await releaseFailedNutritionHydrationAction(original, assert); assert(); replace(null); setError(null); return true; }
    catch (cause) { try { assert(); setError(cause instanceof Error ? cause.message : 'outcome_unknown'); } catch { /* retired */ } return false; }
    finally { try { assert(); lock.current = false; setBusy(false); } catch { /* retired */ } }
  };
  return { pending, ready, busy, error, execute, recover, restoreReceipt, acknowledge, releaseFailure, captureCurrent };
}
